class KtProductAnatomy extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.buttons = [...this.querySelectorAll('[data-kt-anatomy-button]')];
    this.panels = [...this.querySelectorAll('[data-kt-anatomy-panel]')];
    if (!this.buttons.length || !this.panels.length) return;
    this.buttons.forEach((button, index) => {
      button.addEventListener('click', () => this.activate(button));
      button.addEventListener('keydown', (event) => {
        const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
        if (!keys.includes(event.key)) return;
        event.preventDefault();
        let nextIndex = index;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = this.buttons.length - 1;
        if (['ArrowRight', 'ArrowDown'].includes(event.key)) nextIndex = (index + 1) % this.buttons.length;
        if (['ArrowLeft', 'ArrowUp'].includes(event.key)) nextIndex = (index - 1 + this.buttons.length) % this.buttons.length;
        const next = this.buttons[nextIndex];
        this.activate(next);
        next.focus();
      });
    });
    this.activate(this.buttons.find((button) => button.getAttribute('aria-pressed') === 'true') || this.buttons[0]);
    this.dataset.ready = 'true';
  }

  activate(button) {
    const panelId = button.getAttribute('aria-controls');
    this.buttons.forEach((item) => {
      const active = item === button;
      item.setAttribute('aria-pressed', String(active));
      item.tabIndex = active ? 0 : -1;
    });
    this.panels.forEach((panel) => { panel.hidden = panel.id !== panelId; });
  }
}

class KtFloatingProductStage extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.signals = [...this.querySelectorAll('[data-kt-signal]')];
    if (!this.signals.length) return;

    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.handleToggle = (event) => {
      const signal = event.target;
      if (!(signal instanceof HTMLDetailsElement) || !signal.open) return;

      this.signals.forEach((item) => {
        if (item !== signal) item.open = false;
      });
      requestAnimationFrame(() => this.positionDetail(signal));
    };
    this.handleResize = () => {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => this.positionOpenDetails());
    };

    this.signals.forEach((signal) => signal.addEventListener('toggle', this.handleToggle));
    window.addEventListener('resize', this.handleResize, { passive: true });
    this.dataset.ready = 'true';
  }

  disconnectedCallback() {
    this.signals?.forEach((signal) => signal.removeEventListener('toggle', this.handleToggle));
    window.removeEventListener('resize', this.handleResize);
    cancelAnimationFrame(this.resizeFrame);
    delete this.dataset.ready;
  }

  positionOpenDetails() {
    this.signals?.forEach((signal) => {
      if (signal.open) this.positionDetail(signal);
      else {
        signal.style.removeProperty('--kt-detail-shift-x');
        signal.style.removeProperty('--kt-detail-shift-y');
      }
    });
  }

  positionDetail(signal) {
    signal.style.removeProperty('--kt-detail-shift-x');
    signal.style.removeProperty('--kt-detail-shift-y');
    if (!this.mobileQuery.matches) return;

    const detail = signal.querySelector('.kt-signal__detail');
    if (!detail) return;

    const stageRect = this.getBoundingClientRect();
    const detailRect = detail.getBoundingClientRect();
    const inset = 12;
    const safeLeft = Math.max(stageRect.left + inset, inset);
    const safeRight = Math.min(stageRect.right - inset, window.innerWidth - inset);
    const stickyBar = document.querySelector('.sticky-add-to-cart__bar[data-stuck="true"]');
    const stickyTop = stickyBar?.getBoundingClientRect().top ?? window.innerHeight;
    const safeTop = Math.max(stageRect.top + inset, inset);
    const safeBottom = Math.min(stageRect.bottom - inset, stickyTop - inset, window.innerHeight - inset);
    let shift = 0;
    let shiftY = 0;

    if (detailRect.left < safeLeft) shift += safeLeft - detailRect.left;
    if (detailRect.right + shift > safeRight) shift -= detailRect.right + shift - safeRight;
    if (detailRect.bottom > safeBottom) shiftY -= detailRect.bottom - safeBottom;
    if (detailRect.top + shiftY < safeTop) shiftY += safeTop - (detailRect.top + shiftY);
    signal.style.setProperty('--kt-detail-shift-x', `${Math.round(shift)}px`);
    signal.style.setProperty('--kt-detail-shift-y', `${Math.round(shiftY)}px`);
  }
}

class KtLocalizedVideos extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.tabs = [...this.querySelectorAll('[data-kt-video-tab]')];
    this.panels = [...this.querySelectorAll('[data-kt-video-panel]')];
    const languages = new Set(this.panels.map((panel) => panel.dataset.ktVideoPanel));
    this.tabs.forEach((tab) => {
      const language = tab.dataset.ktVideoTab;
      if (!languages.has(language)) tab.hidden = true;
      tab.addEventListener('click', () => this.activate(language));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const visibleTabs = this.tabs.filter((item) => !item.hidden);
        const current = visibleTabs.indexOf(tab);
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const next = visibleTabs[(current + direction + visibleTabs.length) % visibleTabs.length];
        this.activate(next.dataset.ktVideoTab);
        next.focus();
      });
    });
    const requested = languages.has(this.dataset.initialLanguage) ? this.dataset.initialLanguage : this.panels[0]?.dataset.ktVideoPanel;
    if (requested) this.activate(requested);
    this.dataset.ready = 'true';
  }

  activate(language) {
    this.tabs.forEach((tab) => {
      const active = tab.dataset.ktVideoTab === language;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    this.panels.forEach((panel) => { panel.hidden = panel.dataset.ktVideoPanel !== language; });
  }
}
class KtP10SizeGuide extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dialog = this.querySelector('.kt-p10-sizing__dialog');
    if (!this.dialog) return;
    this.closeButton = this.dialog.querySelector('[data-kt-size-guide-close]');
    this.handleTrigger = (event) => {
      const trigger = event.target.closest('a[href="#p10-size-guide"]');
      if (!trigger || this.dialog.open) return;
      event.preventDefault();
      this.trigger = trigger;
      this.dialog.showModal();
      this.closeButton?.focus();
    };
    this.handleClose = () => {
      if (this.trigger?.isConnected) this.trigger.focus();
      this.trigger = null;
    };
    document.addEventListener('click', this.handleTrigger);
    this.closeButton?.addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('close', this.handleClose);
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
    this.dataset.ready = 'true';
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleTrigger);
  }
}
class KtFocusCarousel extends HTMLElement{connectedCallback(){if(this.dataset.ready)return;this.dataset.ready='true';this.viewport=this.querySelector('[data-kt-carousel-viewport]');this.slides=[...this.querySelectorAll('[data-kt-carousel-slide]')];this.status=this.querySelector('[data-kt-carousel-status]');this.querySelector('[data-kt-carousel-prev]')?.addEventListener('click',()=>this.move(-1));this.querySelector('[data-kt-carousel-next]')?.addEventListener('click',()=>this.move(1));this.viewport?.addEventListener('keydown',event=>{if(event.key==='ArrowLeft'){event.preventDefault();this.move(-1)}if(event.key==='ArrowRight'){event.preventDefault();this.move(1)}});this.observer=new IntersectionObserver(entries=>{const visible=entries.filter(entry=>entry.isIntersecting).sort((a,b)=>b.intersectionRatio-a.intersectionRatio)[0];if(visible)this.setActive(this.slides.indexOf(visible.target))},{root:this.viewport,threshold:[.55,.75]});this.slides.forEach(slide=>this.observer.observe(slide));this.setActive(0)}move(direction){const current=Math.max(0,this.slides.findIndex(slide=>slide.classList.contains('is-active')));const next=Math.min(this.slides.length-1,Math.max(0,current+direction));this.slides[next]?.scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth',block:'nearest',inline:'center'});this.setActive(next)}setActive(index){this.slides.forEach((slide,i)=>slide.classList.toggle('is-active',i===index));if(this.status)this.status.textContent=`${index+1} / ${this.slides.length}`}}
class KtStepsSequence extends HTMLElement{connectedCallback(){if(this.dataset.ready||matchMedia('(prefers-reduced-motion: reduce)').matches)return;this.dataset.ready='true';this.dataset.enhanced='true';this.observer=new IntersectionObserver(entries=>{if(entries.some(entry=>entry.isIntersecting)){this.dataset.active='true';this.observer.disconnect()}},{threshold:.2});this.observer.observe(this)}}
if(!customElements.get('kt-product-anatomy'))customElements.define('kt-product-anatomy',KtProductAnatomy);if(!customElements.get('kt-floating-product-stage'))customElements.define('kt-floating-product-stage',KtFloatingProductStage);if(!customElements.get('kt-localized-videos'))customElements.define('kt-localized-videos',KtLocalizedVideos);if(!customElements.get('kt-p10-size-guide'))customElements.define('kt-p10-size-guide',KtP10SizeGuide);if(!customElements.get('kt-focus-carousel'))customElements.define('kt-focus-carousel',KtFocusCarousel);if(!customElements.get('kt-steps-sequence'))customElements.define('kt-steps-sequence',KtStepsSequence);
